import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { mdiTune, mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import get from 'lodash/get';
import { isMobileOnly as isMobile } from 'react-device-detect';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import { makeApiCall, isWithinTradingHours } from '../../../helpers/utils';
import { useInterval } from '../../../helpers/custom-hooks';
import { DELIVERY_FILTERS, MOST_TRADED_FILTERS } from '../../../constants';
import { formatIndianAmount } from '../../../helpers/utils';

import './stock-delivery.m.css';

/* -------------------- COMPONENT -------------------- */

const StockDelivery = ({
  widget = false,
  widgetRows = 3,
  statsWidget = false,
  darkMode = false,
  lightMode = false,
  customClass = '',
  fullWidget = false,
}) => {
  /* -------------------- REDUX -------------------- */
  const pageType = useSelector((state) => get(state, ['qt', 'pageType']));
  const stockDetails = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'stocks_pages']),
  );
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  /* -------------------- STATE -------------------- */

  const isMostTradedPage = pageType === stockDetails?.most_traded?.route;
  const allFilters = isMostTradedPage ? MOST_TRADED_FILTERS : DELIVERY_FILTERS;
  const [showFilter, setShowFilter] = useState(false);
  const [data, setData] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showNoDataImage, setShowNoDataImage] = useState(false);
  const [selectedSort, setSelectedSort] = useState(allFilters[0]);
  const [appliedSort, setAppliedSort] = useState(allFilters[0]);
  const [isSorting, setIsSorting] = useState(false);
  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);

  /* -------------------- INTERSECTION OBSERVER -------------------- */

  const [ref, inView] = useInView({
    threshold: 0.1,
  });

  /* -------------------- API FETCH -------------------- */

  const fetchMostTradedStocks = useCallback(
    async (showLoader = false) => {
      if (!pageType || isFetchingRef.current) return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;
      isFetchingRef.current = true;

      try {
        if (showLoader) {
          setIsInitialLoading(true);
        }

        const endpoint = isMostTradedPage
          ? stockDetails?.most_traded?.endpoint
          : stockDetails?.stock_delivery?.endpoint;

        const response = await fetch(endpoint, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const res = await response.json();
        if (!Array.isArray(res)) {
          setShowNoDataImage(true);
          setData([]);
          return;
        }

        const mappedData = res.map((item) =>
          isMostTradedPage
            ? {
                name: item?.symbol ?? '',
                short_name: item?.name ?? '',
                symbol: item?.symbol ?? '',
                lastPrice: Number(item?.lastPrice) || 0,
                percentChange: Number(item?.percentChange) || 0,
                volume: `${formatIndianAmount(item?.totalAccVol ?? 0)}/${formatIndianAmount(item?.avgVolume ?? 0)}`,
                turnover: (Number(item?.totalAccVol) || 0) * (Number(item?.lastPrice) || 0),
                mkt_cap: (Number(item?.shareOutstanding) || 0) * (Number(item?.lastPrice) || 0),
              }
            : {
                name: item?.name ?? '',
                short_name: item?.isin ?? '',
                isin: item?.isin ?? '',
                lastPrice: Number(item?.lastPrice) || 0,
                percentChange: Number(item?.percentChange) || 0,
                volume: Number(item?.totalAccVol) || 0,
                delivery: Number(item?.totalDelVol) || 0,
                thirtyDay: `${formatIndianAmount(item?.avgVolume ?? 0)}/${formatIndianAmount(item?.avgDelVolume ?? 0)}`,
              },
        );

        setData(mappedData);
        setShowNoDataImage(false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Stock Delivery API error:', err);
          setShowNoDataImage(true);
        }
      } finally {
        setIsInitialLoading(false);
        isFetchingRef.current = false;
      }
    },
    [pageType, isMostTradedPage, stockDetails],
  );

  /* -------------------- LOAD ON SCROLL INTO VIEW -------------------- */

  useEffect(() => {
    if (!inView || !pageType) return;
    fetchMostTradedStocks(true);
  }, [inView, pageType]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (showFilter) {
      // Disable background scroll
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      // Enable background scroll
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [showFilter]);

  /* -------------------- SORTING LIST -------------------- */

  const applySorting = () => {
    if (selectedSort.id === appliedSort.id) return;

    setIsSorting(true);

    setTimeout(() => {
      setAppliedSort(selectedSort);
      setIsSorting(false);
      setShowFilter(false);

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }, 300);
  };

  /* -------------------- MARKET HOURS REFRESH -------------------- */

  useInterval(() => {
    if (document.hidden) return;

    const isMarketOpen = isWithinTradingHours(tradingHolidays, specialTradingDays);

    if (isMarketOpen) {
      fetchMostTradedStocks(false);
    }
  }, 60000);

  /* -------------------- SORTED DATA -------------------- */

  const sortedData = useMemo(() => {
    if (!appliedSort?.key) return data;

    const { key, type } = appliedSort;

    return [...data].sort((a, b) => {
      if (type === 'string') {
        return a[key].localeCompare(b[key]);
      }
      return Number(b[key]) - Number(a[key]);
    });
  }, [data, appliedSort]);

  /* -------------------- FILTER UI (UNCHANGED) -------------------- */

  const createFilters = (mode) => (
    <div styleName="filters-ctx" className={`${mode === 'desktop' ? 'desktop-only' : ''}`}>
      <span styleName="filters-title">Sort By</span>
      <div styleName="filters">
        {allFilters.map((filter) => (
          <div key={filter.id} styleName="filter-item">
            <input
              styleName="radio-input"
              type="radio"
              id={`sort_${filter.id}`}
              name="filter"
              checked={selectedSort.id === filter.id}
              disabled={isInitialLoading || isSorting || data.length === 0}
              onChange={() => setSelectedSort(filter)}
            />
            <label htmlFor={`sort_${filter.id}`} styleName="filter-name">
              {filter.name}
            </label>
          </div>
        ))}
        <div styleName="apply-sort" onClick={() => applySorting()}>
          Apply
        </div>
      </div>
    </div>
  );

  /* -------------------- STOCK CLICK -------------------- */

  const takeToStockPage = (stock) => {
    makeApiCall(`/api/v1/entities?type=stock&q=${stock.name}&limit=10`).then((res) => {
      const entity = isMostTradedPage
        ? res?.entities?.find((ent) => ent['nse-code'] === stock.symbol)
        : res?.entities?.find((ent) => ent.isin === stock.isin);

      if (entity) {
        window.open(`/stock/${entity['stock-id']}/${entity.slug.replace('-stock', '')}`, '_blank');
      } else {
        window.open('/markets', '_blank');
      }
    });
  };

  /* -------------------- SHIMMER STATE -------------------- */

  const ShimmerValue = () => <span styleName="shimmer-line" />;

  /* -------------------- RENDER (UNCHANGED) -------------------- */

  return (
    <>
      <div
        ref={ref}
        styleName={`stock-delivery-ctx ${widget ? 'white-bg' : ''} ${
          darkMode ? 'dark-mode' : ''
        } ${(widgetRows === 4 && !lightMode) || fullWidget ? 'widget-mode' : ''} ${
          statsWidget ? 'custom-padding' : ''
        } ${customClass} ${fullWidget ? 'full-widget-bg' : ''}`}
      >
        <div
          styleName={`stock-delivery-inner-ctx ${widget ? 'widget-view' : ''}`}
          className="container"
        >
          <div styleName="title-ctx">
            {isMostTradedPage
              ? stockDetails?.most_traded?.name
              : stockDetails?.stock_delivery?.name}
          </div>

          <div styleName="content-body">
            {!widget && createFilters('desktop')}
            <div styleName="stock-list">
              {/* DATA ROWS */}
              {!isInitialLoading &&
                !isSorting &&
                sortedData.length > 0 &&
                sortedData.map((item, index) => (
                  <div styleName="stock-row" key={index} onClick={() => takeToStockPage(item)}>
                    <div styleName="cell name">
                      <div styleName="title">{item.name}</div>
                      <div styleName="sub">
                        {!isMostTradedPage && <span>ISIN :</span>} {item.short_name}
                      </div>
                    </div>

                    <div styleName="cell">
                      <span styleName="label">Last Price</span>
                      <span styleName="value">
                        ₹{formatIndianAmount(item.lastPrice)}
                        <span
                          styleName={`change ${item.percentChange > 0 ? 'positive' : 'negative'}`}
                        >
                          {item.percentChange > 0
                            ? ` +${item.percentChange.toFixed(2)}%`
                            : ` ${item.percentChange.toFixed(2)}%`}
                        </span>
                      </span>
                    </div>

                    <div styleName="cell">
                      <span styleName="label">{isMostTradedPage ? 'Turnover' : 'Volume'}</span>
                      <span styleName="value">
                        ₹{formatIndianAmount(isMostTradedPage ? item.turnover : item.volume)}
                      </span>
                    </div>

                    {!isMobile && (
                      <>
                        <div styleName="cell" className="desktop-only">
                          <span styleName="label">
                            {' '}
                            {isMostTradedPage ? 'Mkt Cap' : 'Delivery'}
                          </span>
                          <span styleName="value">
                            ₹{formatIndianAmount(isMostTradedPage ? item.mkt_cap : item.delivery)}
                          </span>
                        </div>

                        <div styleName="cell" className="desktop-only">
                          <span styleName="label">
                            {' '}
                            {isMostTradedPage ? 'Vol / 30D Vol' : '30DA Vol./Delivery'}
                          </span>
                          <span styleName="value">
                            {' '}
                            {isMostTradedPage ? item.volume : item.thirtyDay}
                          </span>
                        </div>

                        <Icon
                          className="desktop-only"
                          styleName="cell arrow"
                          path={mdiChevronRight}
                          size="22px"
                          color={darkMode ? '#FFFFFF' : '#a2abb1'}
                        />
                      </>
                    )}
                  </div>
                ))}

              {/* SHIMMER ROWS (same structure) */}
              {(isInitialLoading || isSorting) &&
                Array.from({ length: widget ? widgetRows : 20 }).map((_, index) => (
                  <div styleName="stock-row" key={index}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        styleName={`cell ${index === 0 ? 'name' : ''}`}
                        className={index === 3 || index === 4 ? 'desktop-only' : ''}
                      >
                        <div styleName="title">
                          <ShimmerValue />
                        </div>
                        <div styleName="sub">
                          <ShimmerValue />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

              {/* NO DATA */}
              {!isInitialLoading && !isSorting && data.length === 0 && showNoDataImage && (
                <div styleName="no-stocks-ctx">
                  <img src="/images/lighthouse-no-bookmark.svg" alt="" styleName="not-found-img" />
                  <span styleName="not-found-title">No Result(s) Found</span>
                  <span styleName="not-found-msg">for the search term you're looking for</span>
                </div>
              )}
            </div>
          </div>

          {isMobile && !widget && (
            <div styleName="mobile-filter" onClick={() => setShowFilter(true)}>
              <Icon path={mdiTune} size="28px" color="#FFFFFF" />
            </div>
          )}
        </div>

        {showFilter && (
          <>
            <div styleName="pop-up-filter">{createFilters('mobile')}</div>
            <div styleName="popover-shadow" onClick={() => setShowFilter(false)} />
          </>
        )}
      </div>
    </>
  );
};

export default StockDelivery;
